const TIANDITU_API_BASE = 'https://api.tianditu.gov.cn/v2';
const CHINA_GB_CODE = '156000000';
const districtRequestCache = new Map<string, Promise<TiandituDistrict>>();

export const getTiandituToken = () => window.TIANDITU_TOKEN?.trim();

const requireTiandituToken = () => {
  const token = getTiandituToken();
  if (!token) {
    throw new Error('缺少天地图 Key，请在 dynamic/config.js 中配置 window.TIANDITU_TOKEN');
  }
  return token;
};

const toTiandituGbCode = (code?: string) => {
  const normalizedCode = code?.trim();
  if (!normalizedCode) return CHINA_GB_CODE;
  return /^\d{6}$/.test(normalizedCode) ? `156${normalizedCode}` : normalizedCode;
};

const toAdcode = (gbCode?: string | number) => {
  const normalizedCode = `${gbCode ?? ''}`;
  return /^156\d{6}$/.test(normalizedCode) ? normalizedCode.slice(3) : normalizedCode;
};

const requestTianditu = async <T>(path: string, params: Record<string, string>): Promise<T> => {
  const searchParams = new URLSearchParams({ ...params, tk: requireTiandituToken() });
  const response = await window.fetch(`${TIANDITU_API_BASE}${path}?${searchParams.toString()}`);
  if (!response.ok) {
    throw new Error(`天地图服务请求失败（${response.status}）`);
  }
  return response.json();
};

interface TiandituAdministrativeItem {
  name: string;
  gb: string;
  level: number;
  children?: TiandituAdministrativeItem[];
}

interface TiandituAdministrativeResponse {
  status?: number;
  message?: string;
  code?: number;
  msg?: string;
  data?: {
    district?: TiandituAdministrativeItem[];
  };
}

export interface TiandituDistrict {
  name: string;
  adcode: string;
  level: 'country' | 'province' | 'city' | 'district';
  districts: TiandituDistrict[];
}

const LEVEL_MAP: Record<number, TiandituDistrict['level']> = {
  5: 'country',
  4: 'province',
  3: 'city',
  2: 'district',
};

const normalizeDistrict = (district: TiandituAdministrativeItem): TiandituDistrict => ({
  name: district.name,
  adcode: toAdcode(district.gb),
  level: LEVEL_MAP[district.level] ?? 'district',
  districts: (district.children ?? []).map(normalizeDistrict),
});

export const getTiandituDistrict = (keyword?: string, childLevel = 3): Promise<TiandituDistrict> => {
  const gbCode = toTiandituGbCode(keyword);
  const normalizedChildLevel = Math.min(3, Math.max(0, childLevel));
  const cacheKey = `${gbCode}:${normalizedChildLevel}`;
  const cachedRequest = districtRequestCache.get(cacheKey);
  if (cachedRequest) return cachedRequest;

  const request = requestTianditu<TiandituAdministrativeResponse>('/administrative', {
    keyword: gbCode,
    childLevel: `${normalizedChildLevel}`,
    extensions: 'false',
  }).then(response => {
    if (response.status !== 200 || !response.data?.district?.length) {
      throw new Error(response.message ?? response.msg ?? '天地图行政区划查询失败');
    }
    return normalizeDistrict(response.data.district[0]);
  });

  districtRequestCache.set(cacheKey, request);
  request.catch(() => districtRequestCache.delete(cacheKey));
  return request;
};

interface TiandituSearchStatus {
  infocode?: number | string;
  cndesc?: string;
}

interface TiandituPoi {
  hotPointID?: string;
  name?: string;
  address?: string;
  lonlat?: string;
  province?: string;
  provinceCode?: string;
  city?: string;
  cityCode?: string;
  county?: string;
  countyCode?: string;
}

interface TiandituSearchResponse {
  pois?: TiandituPoi[];
  status?: TiandituSearchStatus;
  code?: number;
  msg?: string;
}

export interface TiandituAddressTip {
  id?: string;
  name?: string;
  address?: string;
  location?: string;
  district?: string;
  adcode?: string;
}

export const searchTiandituAddress = async (
  keyword: string,
  adcode?: string,
  count = 10
): Promise<TiandituAddressTip[]> => {
  const postStr = JSON.stringify({
    keyWord: keyword,
    queryType: 12,
    start: 0,
    count,
    specify: toTiandituGbCode(adcode),
    show: 2,
  });
  const response = await requestTianditu<TiandituSearchResponse>('/search', { postStr, type: 'query' });

  if (response.code) {
    throw new Error(response.msg ?? '天地图地址查询失败');
  }
  if (response.status?.infocode && Number(response.status.infocode) !== 1000) {
    throw new Error(response.status.cndesc ?? response.msg ?? '天地图地址查询失败');
  }

  return (response.pois ?? []).map(poi => {
    const district = [poi.province, poi.city, poi.county].filter(Boolean).join('');
    return {
      id: poi.hotPointID,
      name: poi.name,
      address: poi.address || district,
      location: poi.lonlat,
      district,
      adcode: toAdcode(poi.countyCode ?? poi.cityCode ?? poi.provinceCode),
    };
  });
};
